import { useState } from "react";
import { Shield, User, Mail, Phone, Briefcase, CreditCard, Lock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCustomer } from "@/contexts/CustomerContext";
import { useToast } from "@/hooks/use-toast";

export default function AccountPage() {
  const {
    profile,
    kycStatus,
    kycApproved,
    premiumUnlocked,
    planTier,
    mfaFactor,
    startMfaEnrollment,
    verifyMfaEnrollment,
    disableMfa,
    mfaLoading,
  } = useCustomer();
  const { toast } = useToast();

  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(profile?.phone || "");
  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [saving, setSaving] = useState(false);

  const [currentPasscode, setCurrentPasscode] = useState("");
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [passcodeSaving, setPasscodeSaving] = useState(false);

  const savePasscode = async () => {
    if (newPasscode !== confirmPasscode) {
      toast({ title: "Passcodes do not match", variant: "destructive" });
      return;
    }
    setPasscodeSaving(true);
    try {
      const { platformApi } = await import("@/platform/api");
      await platformApi.setPasscode(newPasscode, currentPasscode || undefined);
      toast({ title: "Passcode saved", description: "Use it next time you sign in." });
      setCurrentPasscode(""); setNewPasscode(""); setConfirmPasscode("");
    } catch (error) {
      toast({
        title: "Could not save the passcode",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setPasscodeSaving(false);
    }
  };

  // MFA enrollment state
  const [mfaSetup, setMfaSetup] = useState<{
    qr?: string;
    secret?: string;
    factorId: string;
    challengeId: string;
  } | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { platformApi } = await import("@/platform/api");
      await platformApi.updateProfile({ full_name: fullName });
      toast({ title: "Profile updated" });
      setEditing(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleStartMfa = async () => {
    const result = await startMfaEnrollment();
    if (result) setMfaSetup(result);
  };

  const handleVerifyMfa = async () => {
    if (!mfaSetup) return;
    await verifyMfaEnrollment(mfaCode, mfaSetup.factorId, mfaSetup.challengeId);
    setMfaSetup(null);
    setMfaCode("");
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Account</h1>
        <p className="text-sm text-muted-foreground">
          Manage your profile, plan, and security settings.
        </p>
      </div>

      {/* Profile */}
      <Card className="border border-border/40 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Profile</CardTitle>
            {!editing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFullName(profile?.full_name || "");
                  setPhone(profile?.phone || "");
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{profile?.full_name || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{profile?.email || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{profile?.phone || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Employment</p>
                  <p className="font-medium">{profile?.employment_status || "—"}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan & KYC */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border border-border/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Plan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Current plan</span>
              <Badge className={premiumUnlocked ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}>
                {premiumUnlocked ? "Premium" : "Standard"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Credit score</span>
              <span className="font-semibold">{profile?.credit_score ?? "—"}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Premium plan is unlocked automatically when KYC is approved and your tier qualifies.
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">KYC Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge
                className={
                  kycApproved
                    ? "bg-success/10 text-success"
                    : kycStatus === "pending"
                      ? "bg-warning/10 text-warning"
                      : "bg-destructive/10 text-destructive"
                }
              >
                {kycStatus || "not submitted"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">National ID</span>
              <span className="font-semibold">{profile?.national_id || "—"}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Passcode */}
      <Card className="border border-border/40 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4" /> Sign-in passcode
          </CardTitle>
          <CardDescription>
            Use your phone number and this passcode to sign in — no SMS needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">Current passcode</Label>
              <Input type="password" inputMode="numeric" maxLength={12}
                value={currentPasscode}
                onChange={(e) => setCurrentPasscode(e.target.value.replace(/\D/g, ""))}
                placeholder="Leave blank if unset" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New passcode</Label>
              <Input type="password" inputMode="numeric" maxLength={12}
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value.replace(/\D/g, ""))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Confirm</Label>
              <Input type="password" inputMode="numeric" maxLength={12}
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value.replace(/\D/g, ""))} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            At least 5 digits, and not something guessable like 12345.
          </p>
          <Button size="sm" disabled={passcodeSaving || newPasscode.length < 5 || !confirmPasscode}
            onClick={savePasscode}>
            {passcodeSaving ? "Saving..." : "Save passcode"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
